import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import dataCache from '../services/DataCache.js';

// Action types
const SIDEBAR_ACTIONS = {
    SET_ACTIVE_ITEM: 'SET_ACTIVE_ITEM',
    SET_IS_OPEN: 'SET_IS_OPEN',
    SET_USER_CLOSED: 'SET_USER_CLOSED',
    SET_LOADING: 'SET_LOADING',
    SET_ERROR: 'SET_ERROR',
    SET_DATA: 'SET_DATA',
    CLEAR_DATA: 'CLEAR_DATA',
    SET_SELECTED_TICKERS: 'SET_SELECTED_TICKERS',
    SET_USER_PREFERENCES: 'SET_USER_PREFERENCES'
};

// Initial state
const initialState = {
    // Core sidebar state
    activeItem: null,
    isOpen: false,
    hasUserClosed: false,
    
    // Loading states for different data types
    loadingStates: {
        profile: false,
        related: false,
        news: false,
        expectations: false,
        logo: false,
        stockPrice: false,
        sentiment: {},
        revenue: false,
        definition: false,
    },
    
    // Error states
    errorStates: {
        profile: null,
        related: null,
        news: null,
        expectations: null,
        logo: null,
        stockPrice: null,
        sentiment: {},
        revenue: null,
        definition: null,
    },
    
    // Cached data
    cachedData: {
        companyProfiles: {},
        companyLogos: {},
        relatedItems: {},
        news: {},
        analystExpectations: {},
        revenueSegments: {},
        sentimentData: {},
        keywordDefinitions: {}
    },
    
    // Chart state
    selectedTickers: new Set(),
    
    // User preferences
    userPreferences: {
        autoExpand: false,
        rememberState: true,
        defaultOpen: false
    }
};

// Reducer function
function sidebarReducer(state, action) {
    switch (action.type) {
        case SIDEBAR_ACTIONS.SET_ACTIVE_ITEM:
            return {
                ...state,
                activeItem: action.payload,
                // Auto-open only if user preferences allow and hasn't manually closed
                isOpen: state.userPreferences.autoExpand && !state.hasUserClosed ? true : state.isOpen
            };
            
        case SIDEBAR_ACTIONS.SET_IS_OPEN:
            return {
                ...state,
                isOpen: action.payload,
                hasUserClosed: !action.payload
            };
            
        case SIDEBAR_ACTIONS.SET_USER_CLOSED:
            return {
                ...state,
                hasUserClosed: action.payload
            };
            
        case SIDEBAR_ACTIONS.SET_LOADING:
            return {
                ...state,
                loadingStates: {
                    ...state.loadingStates,
                    [action.payload.key]: action.payload.value
                }
            };
            
        case SIDEBAR_ACTIONS.SET_ERROR:
            return {
                ...state,
                errorStates: {
                    ...state.errorStates,
                    [action.payload.key]: action.payload.value
                }
            };
            
        case SIDEBAR_ACTIONS.SET_DATA:
            return {
                ...state,
                cachedData: {
                    ...state.cachedData,
                    [action.payload.type]: {
                        ...state.cachedData[action.payload.type],
                        [action.payload.key]: action.payload.data
                    }
                }
            };
            
        case SIDEBAR_ACTIONS.CLEAR_DATA:
            return {
                ...state,
                cachedData: initialState.cachedData,
                loadingStates: initialState.loadingStates,
                errorStates: initialState.errorStates
            };
            
        case SIDEBAR_ACTIONS.SET_SELECTED_TICKERS:
            return {
                ...state,
                selectedTickers: action.payload
            };
            
        case SIDEBAR_ACTIONS.SET_USER_PREFERENCES:
            return {
                ...state,
                userPreferences: {
                    ...state.userPreferences,
                    ...action.payload
                }
            };
            
        default:
            return state;
    }
}

// Create context
const SidebarContext = createContext(null);

// User preferences key
const USER_PREFERENCES_KEY = 'sidebarUserPreferences';
const SIDEBAR_STATE_KEY = 'sidebarState';

// Provider component
export const SidebarProvider = ({ children }) => {
    const [state, dispatch] = useReducer(sidebarReducer, initialState);
    
    // Load user preferences and sidebar state on mount
    useEffect(() => {
        try {
            // Load user preferences
            const savedPreferences = localStorage.getItem(USER_PREFERENCES_KEY);
            if (savedPreferences) {
                const preferences = JSON.parse(savedPreferences);
                dispatch({
                    type: SIDEBAR_ACTIONS.SET_USER_PREFERENCES,
                    payload: preferences
                });
            }
            
            // Load saved sidebar state (open/closed)
            const savedState = localStorage.getItem(SIDEBAR_STATE_KEY);
            if (savedState) {
                const sidebarState = JSON.parse(savedState);
                dispatch({
                    type: SIDEBAR_ACTIONS.SET_IS_OPEN,
                    payload: sidebarState.isOpen || false
                });
                dispatch({
                    type: SIDEBAR_ACTIONS.SET_USER_CLOSED,
                    payload: sidebarState.hasUserClosed || false
                });
            }
        } catch (error) {
            console.warn('Failed to load sidebar state:', error);
        }
    }, []);
    
    // Save user preferences when they change
    useEffect(() => {
        try {
            localStorage.setItem(USER_PREFERENCES_KEY, JSON.stringify(state.userPreferences));
        } catch (error) {
            console.warn('Failed to save sidebar preferences:', error);
        }
    }, [state.userPreferences]);
    
    // Save sidebar state when it changes
    useEffect(() => {
        try {
            const sidebarState = {
                isOpen: state.isOpen,
                hasUserClosed: state.hasUserClosed
            };
            localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(sidebarState));
        } catch (error) {
            console.warn('Failed to save sidebar state:', error);
        }
    }, [state.isOpen, state.hasUserClosed]);
    
    // Action creators
    const setActiveItem = useCallback((item) => {
        console.log('🎯 SidebarContext: Setting active item:', item);
        dispatch({
            type: SIDEBAR_ACTIONS.SET_ACTIVE_ITEM,
            payload: item
        });
    }, []);
    
    const setIsOpen = useCallback((isOpen) => {
        console.log('🎯 SidebarContext: Setting sidebar open:', isOpen);
        dispatch({
            type: SIDEBAR_ACTIONS.SET_IS_OPEN,
            payload: isOpen
        });
    }, []);
    
    const setUserClosed = useCallback((hasUserClosed) => {
        dispatch({
            type: SIDEBAR_ACTIONS.SET_USER_CLOSED,
            payload: hasUserClosed
        });
    }, []);
    
    const setLoading = useCallback((key, value) => {
        dispatch({
            type: SIDEBAR_ACTIONS.SET_LOADING,
            payload: { key, value }
        });
    }, []);
    
    const setError = useCallback((key, value) => {
        dispatch({
            type: SIDEBAR_ACTIONS.SET_ERROR,
            payload: { key, value }
        });
    }, []);
    
    const setData = useCallback((type, key, data) => {
        dispatch({
            type: SIDEBAR_ACTIONS.SET_DATA,
            payload: { type, key, data }
        });
    }, []);
    
    const clearData = useCallback(() => {
        dispatch({
            type: SIDEBAR_ACTIONS.CLEAR_DATA
        });
        // Also clear the cache
        dataCache.clear();
    }, []);
    
    const setSelectedTickers = useCallback((tickers) => {
        dispatch({
            type: SIDEBAR_ACTIONS.SET_SELECTED_TICKERS,
            payload: tickers
        });
    }, []);
    
    const updateUserPreferences = useCallback((preferences) => {
        dispatch({
            type: SIDEBAR_ACTIONS.SET_USER_PREFERENCES,
            payload: preferences
        });
    }, []);
    
    // Cached fetch functions
    const fetchWithCache = useCallback(async (cacheKey, fetchFunction, dataType, identifier) => {
        // Check cache first
        const cached = dataCache.get(cacheKey);
        if (cached !== null) {
            console.log('📦 Using cached data for:', cacheKey);
            setData(dataType, identifier, cached);
            return cached;
        }
        
        // Set loading state
        setLoading(dataType === 'sentiment' ? 'sentiment' : dataType.split('_')[0], true);
        setError(dataType === 'sentiment' ? 'sentiment' : dataType.split('_')[0], null);
        
        try {
            const data = await fetchFunction();
            
            // Cache the result
            dataCache.set(cacheKey, data);
            
            // Update state
            setData(dataType, identifier, data);
            
            return data;
        } catch (error) {
            console.error('❌ Fetch error for:', cacheKey, error);
            setError(dataType === 'sentiment' ? 'sentiment' : dataType.split('_')[0], error.message);
            throw error;
        } finally {
            setLoading(dataType === 'sentiment' ? 'sentiment' : dataType.split('_')[0], false);
        }
    }, [setData, setLoading, setError]);
    
    // Specific fetch functions
    const fetchCompanyProfile = useCallback(async (symbol) => {
        const cacheKey = dataCache.generateKey('company_profile', symbol);
        return fetchWithCache(cacheKey, async () => {
            const response = await fetch(`/company_profile?symbol=${encodeURIComponent(symbol)}`);
            if (!response.ok) throw new Error('Failed to fetch company profile');
            return response.json();
        }, 'companyProfiles', symbol);
    }, [fetchWithCache]);
    
    const fetchCompanyLogo = useCallback(async (symbol) => {
        const cacheKey = dataCache.generateKey('company_logo', symbol);
        return fetchWithCache(cacheKey, async () => {
            const response = await fetch(`/company_logo?symbol=${encodeURIComponent(symbol)}`);
            if (!response.ok) throw new Error('Failed to fetch company logo');
            return response.json();
        }, 'companyLogos', symbol);
    }, [fetchWithCache]);
    
    const fetchRelatedItems = useCallback(async (keyword) => {
        const cacheKey = dataCache.generateKey('related_items', keyword);
        return fetchWithCache(cacheKey, async () => {
            const response = await fetch(`/semantic_search?keyword=${encodeURIComponent(keyword)}&limit=10`);
            if (!response.ok) throw new Error('Failed to fetch related items');
            const data = await response.json();
            
            // Transform the response to match the expected format
            return {
                related_keywords: data.related_keywords || [],
                related_companies: data.related_companies || []
            };
        }, 'relatedItems', keyword);
    }, [fetchWithCache]);
    
    const fetchRelatedNews = useCallback(async (topic) => {
        const cacheKey = dataCache.generateKey('news', topic, { timestamp: Date.now() });
        return fetchWithCache(cacheKey, async () => {
            const response = await fetch(`/related_news?topic=${encodeURIComponent(topic)}`);
            if (!response.ok) throw new Error('Failed to fetch news');
            const data = await response.json();
            return data.articles || [];
        }, 'news', topic);
    }, [fetchWithCache]);
    
    const fetchAnalystExpectations = useCallback(async (symbol, scope = 'current') => {
        const cacheKey = dataCache.generateKey('analyst_expectations', symbol, { scope });
        return fetchWithCache(cacheKey, async () => {
            const response = await fetch(`/analyst_expectations?symbol=${encodeURIComponent(symbol)}&scope=${scope}`);
            if (!response.ok) throw new Error('Failed to fetch expectations');
            return response.json();
        }, 'analystExpectations', `${symbol}_${scope}`);
    }, [fetchWithCache]);
    
    const fetchRevenueSegmentation = useCallback(async (symbol) => {
        const cacheKey = dataCache.generateKey('revenue_segmentation', symbol);
        return fetchWithCache(cacheKey, async () => {
            const response = await fetch(`/revenue_segmentation?symbol=${encodeURIComponent(symbol)}`);
            if (!response.ok) throw new Error('Failed to fetch revenue segmentation');
            const data = await response.json();
            
            // Transform API response to expected format
            if (data.success && data.segments && data.segments.length > 0) {
                const segments = data.segments;
                const latestYear = data.fiscal_years?.[0];
                
                // Group by segment name and get latest year data
                const segmentMap = new Map();
                segments.forEach(segment => {
                    const name = segment.segment || 'Unknown';
                    const year = segment.year;
                    const revenue = segment.revenue || 0;
                    
                    // Only use data from the latest year, or if no year info, use the first occurrence
                    if (!segmentMap.has(name) || (year && year >= (segmentMap.get(name).year || 0))) {
                        segmentMap.set(name, {
                            name,
                            revenue,
                            year,
                            pct: null // Will calculate percentage later
                        });
                    }
                });
                
                // Filter to only latest year's data if we have year information
                let rows = Array.from(segmentMap.values());
                if (latestYear) {
                    rows = rows.filter(row => row.year === latestYear);
                }
                
                // Calculate percentages
                const totalRevenue = rows.reduce((sum, row) => sum + (row.revenue || 0), 0);
                rows.forEach(row => {
                    if (totalRevenue > 0) {
                        row.pct = (row.revenue / totalRevenue) * 100;
                    }
                });
                
                // Sort by revenue descending
                rows.sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
                
                return {
                    latestLabel: latestYear ? `${latestYear}` : null,
                    rows: rows.slice(0, 10) // Limit to top 10 segments
                };
            }
            
            return { latestLabel: null, rows: [] };
        }, 'revenueSegments', symbol);
    }, [fetchWithCache]);
    
    const fetchAnalystSentiment = useCallback(async (symbol) => {
        const cacheKey = dataCache.generateKey('analyst_sentiment', symbol);
        return fetchWithCache(cacheKey, async () => {
            const response = await fetch(`/analyst_sentiment?symbol=${encodeURIComponent(symbol)}`);
            if (!response.ok) throw new Error('Failed to fetch sentiment');
            return response.json();
        }, 'sentimentData', symbol);
    }, [fetchWithCache]);
    
    const fetchKeywordDefinition = useCallback(async (keyword) => {
        const cacheKey = dataCache.generateKey('keyword_definition', keyword);
        return fetchWithCache(cacheKey, async () => {
            // Import the function dynamically to avoid circular dependencies
            const { fetchWikipediaDefinition } = await import('../utils/wikipedia.js');
            return fetchWikipediaDefinition(keyword);
        }, 'keywordDefinitions', keyword);
    }, [fetchWithCache]);
    
    // Context value
    const contextValue = {
        // State
        ...state,
        
        // Actions
        setActiveItem,
        setIsOpen,
        setUserClosed,
        setLoading,
        setError,
        setData,
        clearData,
        setSelectedTickers,
        updateUserPreferences,
        
        // Cached fetch functions
        fetchCompanyProfile,
        fetchCompanyLogo,
        fetchRelatedItems,
        fetchRelatedNews,
        fetchAnalystExpectations,
        fetchRevenueSegmentation,
        fetchAnalystSentiment,
        fetchKeywordDefinition,
        
        // Cache utilities
        getCacheStats: () => dataCache.getStats(),
        invalidateCache: (pattern) => dataCache.invalidate(pattern),
        clearCache: () => dataCache.clear()
    };
    
    return (
        <SidebarContext.Provider value={contextValue}>
            {children}
        </SidebarContext.Provider>
    );
};

SidebarProvider.propTypes = {
    children: PropTypes.node.isRequired
};

// Hook to use the sidebar context
export const useSidebar = () => {
    const context = useContext(SidebarContext);
    if (!context) {
        throw new Error('useSidebar must be used within a SidebarProvider');
    }
    return context;
};

export default SidebarContext;
